import { useMemo } from 'react';
import Navbar from '../../pages/home/components/Navbar';
import Footer from '../../pages/home/components/Footer';
import SEO from '../../components/feature/SEO';
import ServiceHero from './sections/ServiceHero';
import ServiceStats from './sections/ServiceStats';
import ServiceGrid from './sections/ServiceGrid';
import ServiceWhyUs from './sections/ServiceWhyUs';
import ServiceProcess from './sections/ServiceProcess';
import ServiceGallery from './sections/ServiceGallery';
import ServiceReviews from './sections/ServiceReviews';
import ServiceFAQ from './sections/ServiceFAQ';
import ServiceCTA from './sections/ServiceCTA';
import ServiceMap from './sections/ServiceMap';
import type { ServiceConfig } from './types';

interface Props {
  config: ServiceConfig;
}

const BREADCRUMB_LABELS: Record<string, string> = {
  services: 'Services',
  'installation-upgrades': 'Installation & Upgrades',
  'repair-maintenance': 'Repair & Maintenance',
  'safety-compliance': 'Safety & Compliance',
  commercial: 'Commercial',
};

const BUSINESS_STUB = {
  '@type': 'ElectricalContractor',
  name: 'Quantum Electrical Services',
  telephone: '+441529265060',
  email: 'info@quantum-electrical.uk',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '2-7 St Vincents',
    addressLocality: 'Sleaford',
    addressRegion: 'Lincolnshire',
    postalCode: 'NG34',
    addressCountry: 'GB',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 53.0044,
    longitude: -0.4256,
  },
  areaServed: { '@type': 'Place', name: 'Sleaford, Lincolnshire, UK' },
};

export default function ServiceTemplate({ config }: Props) {
  const siteUrl = (import.meta.env.VITE_SITE_URL as string | undefined) ?? '';

  const jsonLd = useMemo(() => {
    const pageUrl = `${siteUrl}${config.canonicalPath}`;

    // Build breadcrumbs from the canonical path
    const parts = config.canonicalPath.split('/').filter(Boolean);
    const breadcrumbItems: object[] = [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
    ];
    let accPath = siteUrl;
    parts.forEach((part, i) => {
      accPath += `/${part}`;
      const name =
        BREADCRUMB_LABELS[part] ??
        part
          .split('-')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' ');
      breadcrumbItems.push({
        '@type': 'ListItem',
        position: i + 2,
        name,
        item: accPath,
      });
    });

    const schemas: object[] = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: `${config.heroTitle} ${config.heroLocation ? '— ' + config.heroLocation : ''}`.trim(),
        description: config.metaDescription,
        url: pageUrl,
        provider: { ...BUSINESS_STUB, url: siteUrl },
        areaServed: { '@type': 'Place', name: 'Sleaford, Lincolnshire, UK' },
        serviceType: 'Electrical Services',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbItems,
      },
    ];

    if (config.faqs && config.faqs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: config.faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: { '@type': 'Answer', text: faq.a },
        })),
      });
    }

    return schemas;
  }, [config, siteUrl]);

  return (
    <div className="bg-[#0A1628]">
      <SEO
        title={config.pageTitle}
        description={config.metaDescription}
        keywords={config.keywords}
        canonicalPath={config.canonicalPath}
        jsonLd={jsonLd}
      />
      <Navbar />
      <main>
        <ServiceHero
          config={{
            heroBadge: config.heroBadge,
            heroTitle: config.heroTitle,
            heroHighlight: config.heroHighlight,
            heroLocation: config.heroLocation,
            heroDesc: config.heroDesc,
            heroImageUrl: config.heroImageUrl,
          }}
        />
        <ServiceStats />
        <ServiceGrid
          heading={config.servicesHeading}
          subheading={config.servicesSubheading}
          services={config.services}
        />
        <ServiceWhyUs items={config.whyUs} />
        <ServiceProcess steps={config.processSteps} />
        <ServiceGallery images={config.gallery} />
        <ServiceReviews />
        {config.mapEmbedSrc && config.mapHeading && (
          <ServiceMap
            heading={config.mapHeading}
            subheading={config.mapSubheading ?? ''}
            embedSrc={config.mapEmbedSrc}
            areaList={config.mapAreaList}
          />
        )}
        <ServiceFAQ
          heading={config.faqHeading}
          subheading={config.faqSubheading}
          faqs={config.faqs}
        />
        <ServiceCTA formUrl={config.formUrl} />
      </main>
      <Footer />
    </div>
  );
}
